import React, { useState } from "react";
import {
  Button,
  ButtonVariant,
  Card,
  Link,
  Text,
  Tooltip,
  SkeletonLoader,
} from "@merge-api/merge-javascript-shared";
import { APICategory, LinkedAccount } from "../../../../../models/Entities";
import useLoadLinkedAccountConditionData from "./hooks/useLoadLinkedAccountConditionData";
import LinkedAccountConditionsList from "./components/LinkedAccountConditionsList";
import useManageLinkedAccountConditions from "./hooks/useManageLinkedAccountConditions";
import LinkedAccountConditionPublishChangesDialog from "./components/LinkedAccountConditionPublishChangesDialog";
import LinkedAccountConditionConfirmDiscardChangesDialog from "./components/LinkedAccountConditionConfirmDiscardChangesDialog";
import LinkedAccountSyncSelectionsTable from "./components/LinkedAccountSyncSelectionsTable/LinkedAccountSyncSelectionsTable";
import LinkedAccountSensitivityLabelsCard from "./components/LinkedAccountSensitivityLabelsCard";
import useLoadLinkedAccountSyncSelections from "./hooks/useLoadLinkedAccountSyncSelections";
import useLoadLinkedAccountPurviewLabels from "./hooks/useLoadLinkedAccountPurviewLabels";
import useLoadLinkedAccountSharepointSyncConfig from "./hooks/useLoadLinkedAccountSharepointSyncConfig";
import useAppContext from "../../../../context/useAppContext";
import { MergeFlagFeature, useMergeFlag } from "../../../../shared/hooks/useMergeFlag";
import EmptyStateWrapper from "../../../../shared/EmptyStateWrapper";
import { isEmpty } from "lodash";
import { MonitorUp } from "lucide-react";
import { SectionHeaderWrapper } from "../../../../shared/SectionHeaderWrapper";
import { FULL_SELECTIVE_SYNC_BILLING_PLANS } from "../../../../../constants";

type LinkedAccountSelectiveSyncPageProps = {
  linkedAccount: LinkedAccount;
};

const LinkedAccountSelectiveSyncPage = ({ linkedAccount }: LinkedAccountSelectiveSyncPageProps) => {
  const { user } = useAppContext();
  // state
  const [publishChangesDialogOpen, setPublishChangesDialogOpen] = useState(false);

  // hooks
  const { loading, remoteLinkedAccountConditions, linkedAccountConditionMeta, refetch } =
    useLoadLinkedAccountConditionData({ linkedAccount });
  const {
    linkedAccountConditions,
    linkedAccountConditionErrors,
    onLinkedAccountConditionChange,
    onLinkedAccountConditionDelete,
    onLinkedAccountConditionAdd,
    publishChanges,
    changes,
    publishing,
    hasChanges,
    validateChanges,
  } = useManageLinkedAccountConditions({
    remoteLinkedAccountConditions,
    linkedAccountConditionMeta,
    linkedAccountId: linkedAccount.id,
    refetch: () => {
      refetch();
      setPublishChangesDialogOpen(false);
    },
  });
  const { linkedAccountSyncSelections } = useLoadLinkedAccountSyncSelections({
    linkedAccount,
  });
  const { enabled: microsoftSensitivityLabelsEnabled } = useMergeFlag({
    feature: MergeFlagFeature.MERGE_FLAG_ENABLE_MICROSOFT_SENSITIVITY_LABELS,
    organizationId: user.organization.id,
  });
  const { enabled: sharepointOneDriveSyncEnabled } = useMergeFlag({
    feature: MergeFlagFeature.MERGE_FLAG_ENABLE_SHAREPOINT_ONEDRIVE_SYNC,
    organizationId: user.organization.id,
  });
  const { linkedAccountPurviewLabels } = useLoadLinkedAccountPurviewLabels({
    linkedAccount,
  });
  const { syncOneDriveContent } = useLoadLinkedAccountSharepointSyncConfig({
    linkedAccount,
  });

  // event handlers
  const onPublishChangesClick = () => {
    const hasErrors = validateChanges();

    if (hasErrors) return;
    setPublishChangesDialogOpen(true);
  };

  const S2_API_CATEGORIES: APICategory[] = [
    APICategory.filestorage,
    APICategory.knowledgebase,
    ...(user.is_channel_picker_enabled ? [APICategory.chat] : []),
  ];

  function isS2APICategory(category: any): category is APICategory {
    return S2_API_CATEGORIES.includes(category);
  }

  // derived state
  const isOutlookAccount =
    linkedAccount?.integration?.slug?.toLowerCase() === "microsoft-outlook-email";
  const showOutlookSensitivityLabels = isOutlookAccount && microsoftSensitivityLabelsEnabled;
  const showPickerSyncSelections = isS2APICategory(linkedAccount.category);
  // this covers the non enterprise customers who were on the s2 flag
  const selectiveSyncEnabled = user.is_selective_sync_enabled;
  const currentTier =
    user.organization.organization_billing_plan?.billing_plan.plan_tier || undefined;
  const showGatedSelectiveSync =
    !selectiveSyncEnabled &&
    !!currentTier &&
    !FULL_SELECTIVE_SYNC_BILLING_PLANS.includes(currentTier);

  const hasDefaultFiltersIfGatedSelectiveSync =
    showGatedSelectiveSync &&
    remoteLinkedAccountConditions &&
    remoteLinkedAccountConditions.length > 0;

  return (
    <>
      <SectionHeaderWrapper
        title="Selective Sync"
        docsUrl="https://help.merge.dev/en/articles/9113654-selective-sync"
      >
        {showOutlookSensitivityLabels ? (
          <LinkedAccountSensitivityLabelsCard
            purviewLabels={linkedAccountPurviewLabels}
            objectTypeText="Emails"
          />
        ) : showPickerSyncSelections ? (
          <LinkedAccountSyncSelectionsTable
            category={linkedAccount.category}
            linkedAccountSyncSelections={linkedAccountSyncSelections}
            linkedAccount={linkedAccount}
            purviewLabels={
              microsoftSensitivityLabelsEnabled ? linkedAccountPurviewLabels : undefined
            }
            syncOneDriveContent={sharepointOneDriveSyncEnabled ? syncOneDriveContent : undefined}
          />
        ) : (
          <Card>
            <div className="flex px-6 py-5 border-b border-gray-10 flex-row items-center justify-between">
              <Text variant="h4">Filters</Text>
              {!showPickerSyncSelections && (
                <Tooltip title={!hasChanges && "No changes to publish"}>
                  <Button
                    variant={ButtonVariant.PrimaryGray}
                    leftIcon={<MonitorUp size={12} />}
                    onClick={onPublishChangesClick}
                    disabled={!hasChanges}
                    size="sm"
                  >
                    Publish changes
                  </Button>
                </Tooltip>
              )}
            </div>
            <div className="flex flex-col pt-5 px-6 pb-6 gap-y-6">
              {loading || !linkedAccountConditionMeta ? (
                <>
                  <SkeletonLoader height={35} fullWidth borderRadius={4} />
                  <SkeletonLoader height={35} fullWidth borderRadius={4} />
                  <SkeletonLoader height={35} fullWidth borderRadius={4} />
                </>
              ) : showGatedSelectiveSync && !hasDefaultFiltersIfGatedSelectiveSync ? (
                <EmptyStateWrapper
                  title="No filters available"
                  subtitle={
                    <>
                      <Link href="/billing" className="text-blue">
                        Upgrade your plan{" "}
                      </Link>
                      to add filters
                    </>
                  }
                />
              ) : linkedAccountConditionMeta && !isEmpty(linkedAccountConditionMeta) ? (
                <LinkedAccountConditionsList
                  linkedAccount={linkedAccount}
                  linkedAccountConditions={linkedAccountConditions}
                  linkedAccountConditionErrors={linkedAccountConditionErrors}
                  linkedAccountConditionMeta={linkedAccountConditionMeta}
                  onLinkedAccountConditionChange={onLinkedAccountConditionChange}
                  onLinkedAccountConditionDelete={onLinkedAccountConditionDelete}
                  onLinkedAccountConditionAdd={onLinkedAccountConditionAdd}
                  showGatedSelectiveSync={showGatedSelectiveSync}
                />
              ) : (
                <EmptyStateWrapper title="No filters have been set for this Linked Account" />
              )}
            </div>
          </Card>
        )}
      </SectionHeaderWrapper>

      <LinkedAccountConditionConfirmDiscardChangesDialog hasChanges={hasChanges} />

      <LinkedAccountConditionPublishChangesDialog
        organizationName={linkedAccount.end_user.organization_name}
        changes={changes}
        open={publishChangesDialogOpen}
        onClose={() => setPublishChangesDialogOpen(false)}
        publishChanges={publishChanges}
        publishing={publishing}
      />
    </>
  );
};

export default LinkedAccountSelectiveSyncPage;
